import React, { useState, useEffect } from "react";
import { HiOutlineXMark } from "react-icons/hi2";
import pmMatrixService from "../../../../services/pmMatrixService";
import { authService } from "../../../../services";
import { toast } from "react-toastify";
import PageLoading from "../../../../components/common/PageLoading";

const PmTaskDetailsPanel = ({
  selectedCell,
  selectedYear,
  onClose,
  onUpdate,
  users,
  tenantId,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    notes: "",
    status: "Scheduled",
    technicianId: "",
  });

  const currentUser = authService.getUser() || {};
  const userRole = (
    currentUser.role?.roleName ||
    currentUser.roleName ||
    ""
  ).toLowerCase();

  const isViewer = userRole.includes("viewer");
  const isTechnician =
    userRole.includes("technician") || userRole.includes("operator");
  const isPlanner =
    userRole.includes("planner") || userRole.includes("manager");
  const isAdmin = userRole.includes("admin") || userRole.includes("super");

  const canEditSchedule = isAdmin || isPlanner;
  const canUpdateExecution = isAdmin || isTechnician || isPlanner;

  useEffect(() => {
    if (selectedCell) {
      const exec = selectedCell.cellData;
      setFormData({
        notes: exec.notes || "",
        status:
          exec.executionStatus ||
          (exec.state === "Blank" ? "Scheduled" : exec.state),
        technicianId: exec.technicianId ? String(exec.technicianId) : "",
      });
    }
  }, [selectedCell]);

  // Auto-select current technician if status is changed to Completed and tech is empty
  useEffect(() => {
    if (
      formData.status === "Completed" &&
      !formData.technicianId &&
      isTechnician
    ) {
      if (currentUser.userId) {
        setFormData((prev) => ({
          ...prev,
          technicianId: String(currentUser.userId),
        }));
      }
    }
  }, [formData.status, isTechnician, currentUser.userId]);

  if (!selectedCell) return null;

  const { plan, task, cellData } = selectedCell;

  const handleToggleSchedule = async () => {
    if (!canEditSchedule)
      return toast.error("You do not have permission to edit the schedule.");
    try {
      setLoading(true);
      const isCurrentlyScheduled = cellData.scheduled;
      await pmMatrixService.toggleSchedule(
        plan.planId,
        task.taskId,
        cellData.month,
        selectedYear,
        !isCurrentlyScheduled,
      );
      toast.success(
        isCurrentlyScheduled ? "Removed from schedule" : "Added to schedule",
      );
      onUpdate();
    } catch (e) {
      toast.error("Failed to update schedule");
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async (e) => {
    e.preventDefault();
    if (!canUpdateExecution)
      return toast.error("You do not have permission to log execution.");
    if (!cellData.matrixScheduleId)
      return toast.error("This task is not scheduled yet.");

    // Validate if status is 'Completed' then technician should be selected
    if (formData.status === "Completed" && !formData.technicianId) {
      return toast.warning("Please select a technician for completed tasks.");
    }

    try {
      setLoading(true);
      await pmMatrixService.logExecution(
        cellData.matrixScheduleId,
        formData.technicianId ? parseInt(formData.technicianId) : null,
        formData.status,
        formData.notes,
      );
      toast.success("Execution updated successfully");
      onUpdate();
    } catch (err) {
      toast.error("Failed to log execution");
    } finally {
      setLoading(false);
    }
  };

  const techName =
    users.find((u) => String(u.userId) === String(cellData.technicianId))
      ?.fullName || "Not Recorded";

  const assetName =
    plan.machineName ||
    plan.assetName ||
    (plan.machineCode
      ? `Asset: ${plan.machineCode}`
      : "Assigned to scheduled equipment");

  return (
    <div className="task-details-panel">
      {loading && (
        <PageLoading variant="overlay" message="Updating task..." />
      )}

      <div className="panel-header">
        <h3>Task Details</h3>
        <button className="close-panel-btn" onClick={onClose}>
          <HiOutlineXMark />
        </button>
      </div>

      <form onSubmit={handleExecute}>
        <div className="panel-section">
          <label>Why (Plan)</label>
          <div className="value">{plan.planName}</div>
        </div>

        <div className="panel-section">
          <label>What (Action)</label>
          <div className="value">{task.taskName}</div>
        </div>

        <div className="panel-section">
          <label>Frequency</label>
          <div className="value">{task.frequency}</div>
        </div>

        <div className="panel-section">
          <label>Where (Asset)</label>
          <div className="value">{assetName}</div>
        </div>

        <div className="panel-section">
          <label>When (Planned)</label>
          <div className="value">
            {cellData.month} - {selectedYear}
          </div>
        </div>

        <div className="panel-section">
          <label>Current State</label>
          <span className={`status-badge status-${cellData.state}`}>
            {cellData.state}
          </span>
        </div>

        {cellData.state === "Completed" && (
          <div className="panel-section">
            <label>When (Executed Date)</label>
            <div className="value">
              {cellData.executionDate
                ? new Date(cellData.executionDate).toLocaleString()
                : "Date Not Recorded"}
            </div>
            <label style={{ marginTop: "8px" }}>Who (Executed By)</label>
            <div className="value">{techName}</div>
          </div>
        )}

        <hr style={{ borderTop: "1px solid #e5e7eb", margin: "12px 0" }} />

        {canEditSchedule && (
          <div className="panel-section">
            <label>Schedule Action</label>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleToggleSchedule}
              style={{ marginTop: "8px" }}
            >
              {cellData.scheduled ? "Remove from Schedule" : "Add to Schedule"}
            </button>
          </div>
        )}

        {cellData.scheduled && !isViewer && (
          <>
            <div className="panel-section" style={{ marginTop: "16px" }}>
              <label>What (Status)</label>
              <select
                className="select-input"
                value={formData.status}
                disabled={!canUpdateExecution}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="Scheduled">Scheduled</option>
                <option value="Completed">Completed</option>
                <option value="Overdue">Overdue</option>
              </select>
            </div>

            <div className="panel-section">
              <label>Who (Technician)</label>
              <select
                className="select-input"
                value={formData.technicianId}
                disabled={!canUpdateExecution}
                onChange={(e) =>
                  setFormData({ ...formData, technicianId: e.target.value })
                }
              >
                <option value="">Select Technician...</option>
                {/* Ensure current user is in list if it's missing (happens if technician lacks permission to see all users) */}
                {isTechnician &&
                  !users.find((u) => u.userId === currentUser.userId) && (
                    <option value={currentUser.userId}>
                      {currentUser.fullName} (You)
                    </option>
                  )}
                {users.map((u) => (
                  <option key={u.userId} value={String(u.userId)}>
                    {u.fullName}
                  </option>
                ))}
              </select>
            </div>

            <div className="panel-section" style={{ flex: 1 }}>
              <label>How (Notes)</label>
              <textarea
                className="textarea-notes"
                placeholder="Details of the work completed or reason for overdue..."
                value={formData.notes}
                readOnly={!canUpdateExecution}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
              />
            </div>

            {canUpdateExecution && (
              <div className="action-buttons">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Execution
                </button>
              </div>
            )}
          </>
        )}
      </form>
    </div>
  );
};

export default PmTaskDetailsPanel;
